import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Lock, Unlock, ShieldCheck, AlertTriangle, RefreshCw, Save, Plus, Trash2, ExternalLink, CreditCard, Mail, MessageSquare, Phone, Calendar, Sparkles } from "lucide-react";

const CATEGORY_LABELS = {
  payment: { label: "Payment Gateway", icon: CreditCard },
  email: { label: "Transactional Email", icon: Mail },
  sms: { label: "SMS", icon: Phone },
  whatsapp: { label: "WhatsApp", icon: MessageSquare },
  google: { label: "Google Calendar", icon: Calendar },
  security: { label: "Registration Security", icon: ShieldCheck },
  ai: { label: "AI / LLM Provider", icon: Sparkles },
};

const SCOPE_LABELS = {
  platform: "MediGenie SaaS — clinic subscription payments go here",
  clinic: "Your clinic — patient payments are settled to your account",
};

export default function IntegrationsCenter({ scope }) {
  const [catalog, setCatalog] = useState([]);
  const [settings, setSettings] = useState([]);
  const [gates, setGates] = useState({});
  const [urls, setUrls] = useState(null);
  const [editing, setEditing] = useState(null); // { category, provider_key, existing? }
  const [busy, setBusy] = useState(false);

  const isPlatform = scope === "platform";
  const apiBase = isPlatform ? "/integrations/platform" : "/integrations/clinic";

  const load = useCallback(async () => {
    try {
      const [c, s, u] = await Promise.all([
        api.get("/integrations/catalog").catch((e) => ({ data: { providers: [] }, _err: e })),
        api.get(apiBase).catch((e) => ({ data: isPlatform ? [] : { settings: [], feature_gates: {} }, _err: e })),
        api.get("/integrations/webhook-urls").catch((e) => ({ data: null, _err: e })),
      ]);
      setCatalog(c.data.providers || []);
      if (isPlatform) {
        setSettings(Array.isArray(s.data) ? s.data : []);
      } else {
        setSettings(s.data.settings || []);
        setGates(s.data.feature_gates || {});
      }
      setUrls(u.data);
      const firstErr = c._err || s._err || u._err;
      if (firstErr) {
        const msg = firstErr?.response?.data?.detail || firstErr?.message || "Failed to load integrations";
        if (firstErr?.response?.status === 403) {
          toast.error("You do not have permission to view integrations.");
        } else {
          toast.error(msg);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load integrations");
    }
  }, [apiBase, isPlatform]);

  useEffect(() => { load(); }, [load]);

  const groups = catalog.reduce((acc, p) => {
    if (isPlatform && p.category === "ai") {/* show AI on platform only */}
    if (!isPlatform && ["ai", "security"].includes(p.category)) return acc;
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  const findSetting = (category, provider_key) =>
    settings.find((s) => s.provider_category === category && s.provider_key === provider_key);

  const startEdit = (category, provider_key) => {
    const existing = findSetting(category, provider_key);
    const meta = catalog.find((c) => c.category === category && c.provider_key === provider_key);
    setEditing({ category, provider_key, meta, existing });
  };

  const saveSetting = async (form) => {
    setBusy(true);
    try {
      if (editing.existing) {
        await api.put(`${apiBase}/${editing.existing.setting_id}`, form);
        toast.success("Updated");
      } else {
        await api.post(apiBase, form);
        toast.success("Saved");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const deleteSetting = async (setting_id, label) => {
    if (!window.confirm(`Delete the ${label} integration? You can re-add it later.`)) return;
    try { await api.delete(`${apiBase}/${setting_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const testSetting = async (setting_id) => {
    try {
      const r = await api.post("/integrations/test", { setting_id });
      if (r.data.status === "success") toast.success(r.data.message || "Test passed");
      else toast.error(r.data.message || "Test failed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="mg-card">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-mg-medical shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-mg-navy text-sm">{isPlatform ? "MediGenie SaaS Integrations" : "Clinic Integrations"}</h3>
            <p className="text-xs text-mg-secondary mt-1">{SCOPE_LABELS[scope]}</p>
            {!isPlatform && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2 inline-flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Patient payments collected through your gateway settle <strong>directly to your account</strong>. MediGenie does not receive patient invoice payments.</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {Object.entries(groups).map(([category, providers]) => {
        const meta = CATEGORY_LABELS[category] || { label: category, icon: Sparkles };
        const Icon = meta.icon;
        // Plan-feature gate for clinic-scope (Pro/Enterprise check)
        const featureKey = {
          payment: "clinic_payment_gateway",
          email: "custom_email_provider",
          sms: "custom_sms_provider",
          whatsapp: "custom_whatsapp_provider",
          google: "google_calendar_sync",
        }[category];
        const locked = !isPlatform && featureKey && gates[featureKey] === false;
        return (
          <div key={category} className="mg-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-mg-navy text-base flex items-center gap-2">
                <Icon className="w-4 h-4 text-mg-medical" /> {meta.label}
              </h3>
              {locked && (
                <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 rounded-full px-2.5 py-1 flex items-center gap-1" data-testid={`locked-${category}`}>
                  <Lock className="w-3 h-3" /> Upgrade to Pro
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {providers.map((p) => {
                const s = findSetting(p.category, p.provider_key);
                const setupStatus = s?.setup_status || (s ? "configured" : "not_configured");
                return (
                  <div key={p.provider_key} className={`border border-slate-200 rounded-xl p-4 ${locked ? "opacity-60" : ""}`} data-testid={`provider-${p.category}-${p.provider_key}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold text-mg-navy text-sm">{p.display_name}</div>
                        <div className="text-[10px] text-mg-muted">{p.category}/{p.provider_key}</div>
                      </div>
                      {s ? (
                        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${s.is_enabled ? "bg-green-50 text-mg-health" : "bg-slate-100 text-slate-500"}`} data-testid={`provider-mode-${p.category}-${p.provider_key}`}>
                          {s.is_enabled ? `${s.mode}` : "off"}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-100 text-slate-500">unconfigured</span>
                      )}
                    </div>
                    {s && (
                      <div className="text-[11px] text-slate-500 mt-2" data-testid={`setup-status-${p.category}-${p.provider_key}`}>
                        Setup: <strong className="text-slate-700">{setupStatus}</strong>
                        {typeof s.webhook_verified === "boolean" ? ` · Webhook: ${s.webhook_verified ? "verified" : "pending"}` : ""}
                      </div>
                    )}
                    {s && s.last_test_status && (
                      <div className={`text-xs mt-2 ${s.last_test_status === "success" ? "text-mg-health" : "text-red-600"}`} data-testid={`status-${p.category}-${p.provider_key}`}>
                        Last test: <strong>{s.last_test_status}</strong> · {s.last_tested_at?.slice(0, 19).replace("T", " ")} {s.last_error ? `· ${s.last_error}` : ""}
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={() => startEdit(p.category, p.provider_key)} disabled={locked} className="text-xs mg-btn-secondary disabled:opacity-50" data-testid={`edit-${p.category}-${p.provider_key}`}>
                        {s ? "Edit" : <><Plus className="w-3.5 h-3.5" /> Configure</>}
                      </button>
                      {s && (
                        <>
                          <button onClick={() => testSetting(s.setting_id)} className="text-xs text-mg-medical font-semibold" data-testid={`test-${p.category}-${p.provider_key}`}>
                            <RefreshCw className="w-3.5 h-3.5 inline" /> Test
                          </button>
                          <button onClick={() => deleteSetting(s.setting_id, p.display_name)} className="text-xs text-red-500 ml-auto">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Webhook URLs */}
      {urls && (
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy text-base flex items-center gap-2 mb-3">
            <ExternalLink className="w-4 h-4 text-mg-medical" /> Webhook URLs
          </h3>
          <p className="text-xs text-mg-muted mb-3">Paste these into your provider dashboards.{isPlatform ? " Set the base URL when moving to a custom production domain." : ""}</p>
          {isPlatform && (
            <WebhookBaseEditor base={urls.base_url} onSaved={load} />
          )}
          <div className="space-y-2 text-xs">
            {isPlatform && Object.entries(urls.platform || {}).map(([k, v]) => (
              <WebhookRow key={k} label={`Platform · ${k}`} url={v} />
            ))}
            {!isPlatform && Object.entries(urls.clinic || {}).map(([k, v]) => (
              <WebhookRow key={k} label={`Clinic · ${k}`} url={v} />
            ))}
            {isPlatform && Object.entries(urls.communications || {}).map(([k, v]) => (
              <WebhookRow key={k} label={`Communications · ${k}`} url={v} />
            ))}
            {isPlatform && Object.entries(urls.google || {}).map(([k, v]) => (
              <WebhookRow key={k} label={`Google · ${k}`} url={v} />
            ))}
          </div>
        </div>
      )}

      {editing && (
        <SettingEditorModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={saveSetting}
          busy={busy}
          scope={scope}
        />
      )}
    </div>
  );
}

function WebhookRow({ label, url }) {
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Copied"); };
  return (
    <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
      <span className="font-mono text-[10px] uppercase tracking-wider text-mg-medical w-32 shrink-0">{label}</span>
      <code className="flex-1 truncate text-slate-700">{url}</code>
      <button onClick={copy} className="text-xs text-mg-medical font-semibold">Copy</button>
    </div>
  );
}

function WebhookBaseEditor({ base, onSaved }) {
  const [value, setValue] = useState(base);
  const [editing, setEditing] = useState(false);
  const save = async () => {
    try { await api.post("/integrations/webhook-base-url", { base_url: value }); toast.success("Base URL updated"); setEditing(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!editing) {
    return (
      <div className="text-xs text-mg-muted mb-3 flex items-center gap-2">
        Base URL: <code className="text-slate-700">{base}</code>
        <button onClick={() => setEditing(true)} className="text-mg-medical font-semibold" data-testid="edit-webhook-base">Change</button>
      </div>
    );
  }
  return (
    <div className="flex gap-2 mb-3">
      <input value={value} onChange={(e) => setValue(e.target.value)} className="mg-input flex-1 text-xs" data-testid="webhook-base-input" />
      <button onClick={save} className="mg-btn-primary text-sm" data-testid="save-webhook-base"><Save className="w-3.5 h-3.5" /> Save</button>
      <button onClick={() => setEditing(false)} className="mg-btn-secondary text-sm">Cancel</button>
    </div>
  );
}

function SettingEditorModal({ editing, onClose, onSave, busy }) {
  const { category, provider_key, meta, existing } = editing;
  const [form, setForm] = useState(() => ({
    provider_category: category,
    provider_key,
    is_enabled: existing?.is_enabled ?? true,
    is_default: existing?.is_default ?? true,
    mode: existing?.mode ?? "test",
    credentials: existing?.credentials ? { ...existing.credentials } : {},
    public_config: existing?.public_config ? { ...existing.public_config } : {},
  }));
  const secretFields = meta?.secret_fields || [];
  const publicFields = meta?.public_fields || [];
  const set = (path, key, value) => setForm((f) => ({ ...f, [path]: { ...f[path], [key]: value } }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-mg-navy text-lg mb-1">{meta?.display_name}</h3>
        <p className="text-xs text-mg-muted mb-4">{category}/{provider_key} · secrets are encrypted at rest and only shown masked after save.</p>
        <div className="space-y-3 text-sm">
          <label className="flex items-center gap-2 text-mg-navy">
            <input type="checkbox" checked={form.is_enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })} data-testid="setting-enabled" /> Enabled
          </label>
          {category !== "system" && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Mode</span>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="mg-input mt-1" data-testid="setting-mode">
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </label>
          )}
          {publicFields.map((f) => (
            <label key={f} className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">{f.replace(/_/g, " ")}</span>
              {typeof form.credentials[f] === "boolean" || (publicFields.includes(f) && ["use_emergent_default", "premium_llm_globally_enabled", "signin_enabled", "calendar_sync_enabled", "calendar_api_enabled", "use_tls"].includes(f)) ? (
                <input type="checkbox" className="block mt-1" checked={!!form.credentials[f]} onChange={(e) => set("credentials", f, e.target.checked)} data-testid={`field-${f}`} />
              ) : (
                <input value={form.credentials[f] ?? ""} onChange={(e) => set("credentials", f, e.target.value)} className="mg-input mt-1" data-testid={`field-${f}`} />
              )}
            </label>
          ))}
          {secretFields.map((f) => (
            <label key={f} className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">{f.replace(/_/g, " ")} <span className="text-red-500">●</span></span>
              <input
                type="password"
                value={form.credentials[f] ?? ""}
                onChange={(e) => set("credentials", f, e.target.value)}
                placeholder={existing?.credentials?.[f] ? `Current: ${existing.credentials[f]} — leave blank to keep` : "Paste secret"}
                className="mg-input mt-1 font-mono"
                data-testid={`field-${f}`}
              />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="mg-btn-secondary text-sm">Cancel</button>
          <button onClick={() => onSave(form)} disabled={busy} className="mg-btn-primary text-sm disabled:opacity-50" data-testid="save-setting">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
